import { useAuth } from "../auth/AuthContext";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

export default function CourseList() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [title, setTitle] = useState("");
    const [courses, setCourses] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) return;

        const loadCourses = async () => {
            const q = query(
                collection(db, "courses"),
                where("teacherId", "==", user.uid)
            );
            const snap = await getDocs(q);
            const courseData = snap.docs.map(d => ({
                id: d.id,
                ...d.data(),
                createdAt: d.data().createdAt?.toDate() || new Date(),
            }));
            setCourses(courseData);
            setLoading(false);
        };

        loadCourses();
    }, [user]);

    const goToCreateCourse = () => {
        navigate("/courses/new", { state: { title } });
    };

    const formatDate = (date: Date) => {
        return new Intl.DateTimeFormat('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(date);
    };

    const calculateActivity = (createdAt: Date) => {
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - createdAt.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays <= 7) return "Hoch";
        if (diffDays <= 30) return "Mittel";
        return "Niedrig";
    };

    const getActivityColor = (activity: string) => {
        switch(activity) {
            case "Hoch": return "#22c55e";
            case "Mittel": return "#f59e0b";
            case "Niedrig": return "#ef4444";
            default: return "#94a3b8";
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white text-gray-800 relative overflow-x-hidden">
            <div className="max-w-7xl mx-auto p-6 relative z-10">
                <header className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-12 p-8 bg-white/90 backdrop-blur-sm rounded-3xl shadow-md border border-gray-200 animate-float-in">
                    <div>
                        <h1 className="text-4xl lg:text-5xl font-bold mb-2 text-gray-800">Meine Kurse</h1>
                        <p className="text-gray-500">{courses.length} {courses.length === 1 ? 'Kurs' : 'Kurse'} verfügbar</p>
                    </div>

                    <div className="flex gap-4 w-full lg:w-auto">
                        <input
                            className="flex-1 lg:flex-none lg:w-80 px-6 py-4 rounded-2xl border border-gray-300 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all"
                            placeholder="Neuen Kurs erstellen..."
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && goToCreateCourse()}
                        />
                        <button
                            className="w-16 h-16 flex-shrink-0 rounded-2xl bg-gradient-to-br from-green-500 to-green-600 text-white hover:shadow-2xl hover:shadow-green-500/40 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center animate-pulse-subtle"
                            onClick={goToCreateCourse}
                            disabled={!title.trim()}
                            title="Kurs erstellen"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </header>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-48 bg-gray-200 rounded-2xl animate-pulse"></div>
                        ))}
                    </div>
                ) : courses.length === 0 ? (
                    <div className="text-center p-16 bg-white/90 backdrop-blur-sm rounded-3xl shadow-md border border-gray-200 mb-10 animate-slide-up">
                        <div className="inline-block p-6 bg-gray-100 rounded-3xl mb-6">
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-3">Noch keine Kurse vorhanden</h3>
                        <p className="text-gray-500 mb-8">Erstelle deinen ersten Kurs, um mit dem Unterricht zu beginnen</p>
                        <button
                            className="px-8 py-4 rounded-2xl border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-green-500 hover:text-green-600 transition-all duration-300 font-semibold flex items-center gap-2 mx-auto"
                            onClick={() => navigate("/courses/new")}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>Kurs Erstellen</span>
                        </button>
                    </div>
                ) : (
                    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {courses.map((course, index) => {
                            const activity = calculateActivity(course.createdAt);
                            const activityColor = getActivityColor(activity);

                            return (
                                <article
                                    key={course.id}
                                    className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-3xl p-6 cursor-pointer hover:-translate-y-2 hover:border-green-500 hover:shadow-xl transition-all duration-300 animate-float-in"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                    onClick={() => navigate(`/courses/${course.id}`)}
                                >
                                    <div className="flex justify-between items-start mb-4">
                                        <h3 className="text-xl font-bold text-gray-800 pr-4 truncate">{course.title}</h3>
                                        <div className={`w-3 h-3 rounded-full ${course.active ? 'bg-green-500' : 'bg-red-500'} relative`}>
                                            {course.active && (
                                                <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75"></div>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-gray-600 mb-6 line-clamp-3">
                                        {course.description || "Keine Beschreibung verfügbar"}
                                    </p>
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-4 border-t border-gray-200">
                                        <span className="text-sm text-gray-500">
                                            Erstellt am {formatDate(course.createdAt)}
                                        </span>
                                        <div className="flex gap-2">
                                            <span className="px-3 py-1.5 text-sm font-medium rounded-full bg-gray-100 border border-gray-200 flex items-center gap-1.5"
                                                  style={{ color: activityColor }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                {activity} Aktiv
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-gray-200/50 flex justify-end">
                                        <span className="text-xs text-gray-400 font-mono">
                                            ID: {course.id.substring(0, 8)}...
                                        </span>
                                    </div>
                                </article>
                            );
                        })}
                    </section>
                )}
            </div>
        </div>
    );
}